import { Add, Remove } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

import { columnPickerToggleAllButtonSx } from './analyticsStatsColumnListUtils';
import { ColumnInfoTooltip, columnLabelWithTooltipRowSx } from './ColumnInfoTooltip';

const expandCheckboxSx = { p: 0.25, pointerEvents: 'none', color: 'action.active' } as const;

const listItemButtonSx = {
  py: 0.25,
  minHeight: 32,
  width: '100%',
  alignItems: 'flex-start',
} as const;

type ColumnPickerListItemProps = {
  label: string;
  tooltip?: string | null;
  selected: boolean;
  onToggle: () => void;
  /** When true, row is non-interactive (e.g. bulk editing blocked). */
  disabled?: boolean;
  /** Optional caption shown under the label (e.g. dimension requirement hint). */
  secondaryText?: string;
  /** Slightly stronger label weight for grouping rows such as dimensions. */
  emphasizeLabel?: boolean;
};

export const ColumnPickerListItem = ({
  label,
  tooltip,
  selected,
  onToggle,
  disabled = false,
  secondaryText,
  emphasizeLabel = false,
}: ColumnPickerListItemProps) => (
  <ListItem disablePadding sx={{ minHeight: 0 }}>
    <ListItemButton onClick={onToggle} dense disabled={disabled} sx={listItemButtonSx}>
      <ListItemIcon sx={{ minWidth: 32 }}>
        <Checkbox
          edge="start"
          checked={selected}
          tabIndex={-1}
          disableRipple
          size="small"
          disabled={disabled}
          sx={{ p: 0.25 }}
        />
      </ListItemIcon>
      <Box sx={{ flex: 1, my: 0, minWidth: 0 }}>
        <Box sx={{ ...columnLabelWithTooltipRowSx }}>
          <Typography
            variant="body2"
            component="span"
            fontWeight={emphasizeLabel ? 600 : undefined}
            sx={{ minWidth: 0, pr: 0.5 }}
          >
            {label}
          </Typography>
          {tooltip ? <ColumnInfoTooltip tooltip={tooltip} stopPropagation /> : null}
        </Box>
        {secondaryText ? (
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            sx={{ mt: 0.25, pr: 0.5, lineHeight: 1.3 }}
          >
            {secondaryText}
          </Typography>
        ) : null}
      </Box>
    </ListItemButton>
  </ListItem>
);

type ColumnPickerExpandRowProps = {
  label: string;
  tooltip?: string;
  expanded: boolean;
  onToggle: () => void;
  /** Rendered on the right (e.g. Add all / Remove all), outside the expand hit target. */
  trailingAction?: React.ReactNode;
};

/** Expand/collapse row aligned with checkbox list items (plus/minus icon). */
export const ColumnPickerExpandRow = ({
  label,
  tooltip,
  expanded,
  onToggle,
  trailingAction,
}: ColumnPickerExpandRowProps) => (
  <ListItem disablePadding sx={{ minHeight: 0 }}>
    <Stack
      direction="row"
      alignItems="flex-start"
      sx={{ width: '100%', minWidth: 0, pr: trailingAction ? 0.5 : 0 }}
    >
      <ListItemButton onClick={onToggle} dense sx={{ ...listItemButtonSx, flex: 1, minWidth: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <Checkbox
            edge="start"
            checked={false}
            tabIndex={-1}
            disableRipple
            size="small"
            icon={expanded ? <Remove sx={{ fontSize: 18 }} /> : <Add sx={{ fontSize: 18 }} />}
            sx={expandCheckboxSx}
          />
        </ListItemIcon>
        <Box sx={{ ...columnLabelWithTooltipRowSx, flex: 1, my: 0 }}>
          <Typography variant="body2" component="span" sx={{ minWidth: 0, pr: 0.5 }}>
            {label}
          </Typography>
          {tooltip ? <ColumnInfoTooltip tooltip={tooltip} stopPropagation /> : null}
        </Box>
      </ListItemButton>
      {trailingAction ? <Box sx={{ flexShrink: 0, pt: 0.25 }}>{trailingAction}</Box> : null}
    </Stack>
  </ListItem>
);

type ColumnPickerToggleAllButtonProps = {
  allSelected: boolean;
  onClick: () => void;
  addTitle?: string;
  removeTitle?: string;
  disabled?: boolean;
  /** Set when the button lives inside an accordion summary (staff catalog, etc.). */
  stopPropagation?: boolean;
};

export const ColumnPickerToggleAllButton = ({
  allSelected,
  onClick,
  addTitle = 'Add all columns in this category',
  removeTitle = 'Remove all columns in this category',
  disabled = false,
  stopPropagation = false,
}: ColumnPickerToggleAllButtonProps) => (
  <Button
    size="small"
    variant="text"
    disabled={disabled}
    onClick={(event) => {
      if (stopPropagation) {
        event.stopPropagation();
      }
      onClick();
    }}
    onMouseDown={stopPropagation ? (event) => event.stopPropagation() : undefined}
    startIcon={allSelected ? <Remove sx={{ fontSize: 14 }} /> : <Add sx={{ fontSize: 14 }} />}
    title={allSelected ? removeTitle : addTitle}
    sx={columnPickerToggleAllButtonSx}
  >
    {allSelected ? 'Remove all' : 'Add all'}
  </Button>
);
